import { AnyAction, Middleware } from 'redux';

import { twilioActions } from 'app/middleware/twilio/actions/twilioActions';
import {
  isMobilePWA,
  isPWA,
  pwaMediaQuery,
} from 'app/middleware/visibility/utils/helpers';
import { busylightActions } from 'app/views/layout/views/settings/views/preferences/views/preferencesConnectedDevices/views/busylight/busylight.actions';
import { AppState } from 'bootstrap.store';
import { presenceActions } from 'components/presence/actions/presenceActions';
import { ssrSafeWindow } from 'utils/mediaQuery/utils/ssrSafeWindow';

import { wakeLockManager } from './utils/wakeLockManager';

async function setOrReleaseWakeLock(status: string) {
  if (status === 'working' || status === 'busy') {
    await wakeLockManager.request();
  } else {
    await wakeLockManager.release();
  }
}

export const pwaMiddleware: () => Middleware<unknown, AppState> =
  () => (store) => {
    /** If twilio has established a call connection */
    let hasEstablishedCall = false;

    /** If switched to PWA from a browser handle change */
    ssrSafeWindow
      .matchMedia(pwaMediaQuery)
      .addEventListener('change', async (e) => {
        if (e.matches) {
          await setOrReleaseWakeLock(store.getState().presence.presence.status);
        } else {
          await wakeLockManager.release();
        }
      });

    return (next) => (action: AnyAction) => {
      if (isPWA() && !isMobilePWA()) {
        const state = store.getState();
        switch (action.type) {
          case presenceActions.presenceSetStatusSucceeded.toString(): {
            const status =
              action.payload.data?.presence_status_category ||
              action.payload.params?.new_status ||
              'offline';
            setOrReleaseWakeLock(status);

            break;
          }
          case presenceActions.presenceWsSetOwnStatus.toString(): {
            // Don't update presence status when a twilio call is established
            if (hasEstablishedCall) {
              break;
            }
            setOrReleaseWakeLock(
              action.payload.params?.new_status || 'offline',
            );
            break;
          }
          case presenceActions.presenceGetAllSucceeded.toString(): {
            const agentPresence = action.payload.data.find(
              (presence) => presence.user.id === action.payload.user_id,
            );
            if (agentPresence) {
              setOrReleaseWakeLock(
                agentPresence?.presence?.status || 'offline',
              );
            }
            break;
          }
          case twilioActions.twilioEstablishedCall.toString(): {
            hasEstablishedCall = true;
            break;
          }
          case twilioActions.twilioDisconnectedCall.toString(): {
            hasEstablishedCall = false;
            setOrReleaseWakeLock(state.presence.presence.status);
            break;
          }
          case busylightActions.stopFulfilled.toString(): {
            setOrReleaseWakeLock(state.presence.presence.status);
            break;
          }
        }
      }
      return next(action);
    };
  };
